/**
 * @module lib/election/bubble-chart
 * @description Initializes a provincial breakdown bubble chart for the federal election
 */
import tgamConfig from "config/tgam";
import logger from "utils/logger";
import { getById, getProp, qs, qsa } from "utils/utils";
import ee from "lib/eventemitter";
import { getEntriesByName, mark, measure } from "lib/performance/user-timing";
import { errorStateOverlayEnabled, errorStateOverlayDisabled } from "lib/election/error-state";

const debugTag = "[ELECTION-BUBBLE-CHART]";
const bubbleChartId = "js-election-bubble-chart";
const bubbleClass = "c-election-bubble-chart__bubble";

const partyMappings = {
  LIB: {
    name: "Liberal Party",
    modifier: "lib",
  },
  CON: {
    name: "Conservative Party",
    modifier: "cpc"
  },
  NDP: {
    name: "New Democratic Party",
    modifier: "ndp"
  },
  BQ: {
    name: "Bloc Québécois",
    modifier: "bq"
  },
  GRN: {
    name: "Green Party",
    modifier: "gp"
  },
  OTH: {
    name: "Other",
    modifier: "other"
  }
};

const electionYear = getProp(tgamConfig, "meta.electionYear", "2021");

if (electionYear === "2019") { // PPC was listed as "PPC" in the CP feed for the 2019 election.
  partyMappings.PPC = {
    name: "People's",
    abbr: "ppc",
    modifier: "ppc"
  };
} else { // In 2021, CP returned the People's Party results as "PP".
  partyMappings.PP = {
    name: "People's",
    abbr: "ppc",
    modifier: "ppc"
  };
}

// Needed for result descriptions for screenreaders.
let sortedPartiesByLeadingByRegion = [];// Parties sorted in order from highest number of leading ridings to lowest for the region
let sortedPartiesByElectedByRegion = [];// Parties sorted in order from highest number of elected seats to lowest for the region

let numLeadingByPartyByRegion = [];
let numElectedByPartyByRegion = [];

/**
 * @method init
 * @description Init the bubble chart
 */
export function initElectionBubbleChart() {
  const bubbleChart = getById(bubbleChartId);
  const bubbleChartEl = `#${bubbleChartId}`;

  if (!bubbleChart) {
    logger.info(`${debugTag} Bubble chart container not found`);
    return;
  }
  let manualErrorState = bubbleChart.dataset.errorstate;

  ee.on("election:riding-data-failed", () => {
    errorStateOverlayEnabled(bubbleChartEl);
  });

  ee.on("election:riding-data", data => {
    mark("bubble-chart_started");
    if (manualErrorState === "true") {
      errorStateOverlayEnabled(bubbleChartEl);
    } else {
      errorStateOverlayDisabled(bubbleChartEl);
    }
    logger.info(`${debugTag} Data from API`, data);

    const sortedResultsByRegion = processRidingData(getRidingDataArray(data));
    logger.info(`${debugTag} Final sorted results`, sortedResultsByRegion);

    bubblifyResults(sortedResultsByRegion);
    mark("bubble-chart_done");
    measure("bubble-chart");
    logger.info(`${debugTag} Performance measure result:`, getEntriesByName("bubble-chart"));
  });
}

/**
 * Returns modified/reduced riding data (with region ID, riding, elected, votes, party, status, and color)
 * as an array of objects, representing each riding result.
 * @param {Object} data - JSON object containing federal election ridings data
 * @returns {Array<Object>}
 */
function getRidingDataArray(data) {
  let ridingResultsArray = [];

  for (let riding in data) {
    const ridingResult = {
      regionID: "",
      riding: "",
      elected: "",
      votes: "",
      party: "",
      status: "", // "" for no results
    };

    const ridingData = data[riding];
    let parties = Object.keys(partyMappings);
    parties.pop();// Remove "Other" parties.

    ridingResult.riding = riding;
    ridingResult.regionID = getProp(ridingData, "RegionID", "");
    ridingResult.elected = getProp(ridingData, "Cand[0].E", "No");
    ridingResult.votes = getProp(ridingData, "Cand[0].V", 0);
    ridingResult.party = getProp(ridingData, "Cand[0].PE", "");
    ridingResult.status = getStatus(ridingResult);

    if (!parties.includes(ridingResult.party)) { // Group less "popular" parties under the "Other" party category.
      ridingResult.party = "OTH";
    }

    ridingResultsArray.push(ridingResult);
  }

  return ridingResultsArray;
}

/**
 * Returns sorted array of arrays of riding data JSON objects in order of
 * parties with highest elected number of ridings to lowest followed by
 * parties with highest leading number of ridings to lowest, by region.
 * (E.g. array item number 1 represents province with region ID 1's data, etc.)
 * @param {Array<Object>} ridingResultsArray - array of JSON objects with results for each riding.
 * @returns {Array<Array>}
 */
function processRidingData(ridingResultsArray) {
  let sortedResultsByRegion = [];

  // For each region ID, grab the riding data results and store it as an object in an array
  for (let i = 1; i < 14; i++) {
    const resultsForRegion = ridingResultsArray.filter((ridingData) => dataBelongsToRegion(ridingData, i));
    let sortedPartiesByLeading = [];// Parties sorted in order from highest number of leading ridings to lowest for the region
    let sortedPartiesByElected = [];// Parties sorted in order from highest number of elected seats to lowest for the region
    let sortedPartiesByTotal = [];// Parties sorted in order from highest number of leading & elected seats to lowest for the region

    // Get the elected and leading data for the current region being iterated.
    const electedResultsForRegion = resultsForRegion.filter((ridingData) => isElected(ridingData));
    const leadingResultsForRegion = resultsForRegion.filter((ridingData) => isLeading(ridingData));
    const partyResultsForRegion = resultsForRegion.filter((ridingData) => isLeading(ridingData) || isElected(ridingData));

    // Number of leading and elected candidates in the region by party
    const numLeadingByParty = getPartyCounts(leadingResultsForRegion);
    const numElectedByParty = getPartyCounts(electedResultsForRegion);
    const totalElectedByParty = getPartyCounts(partyResultsForRegion);

    // Sort parties from highest to lowest number of leading/elected ridings/seats.
    sortedPartiesByLeading = getOrderedParties(numLeadingByParty);
    sortedPartiesByElected = getOrderedParties(numElectedByParty);
    sortedPartiesByTotal = getOrderedParties(totalElectedByParty);

    // Needed for result descriptions for screenreaders.
    sortedPartiesByLeadingByRegion.push(sortedPartiesByLeading);
    sortedPartiesByElectedByRegion.push(sortedPartiesByElected);

    numLeadingByPartyByRegion.push(numLeadingByParty);
    numElectedByPartyByRegion.push(numElectedByParty);

    // For testing purposes
    logger.info(`${debugTag} Unsorted results for region ${i}:`, resultsForRegion);

    logger.info(`${debugTag} Leading results for region ${i}:`, leadingResultsForRegion);
    logger.info(`${debugTag} Elected results for region ${i}:`, electedResultsForRegion);
    logger.info(`${debugTag} Total results for region ${i}:`, partyResultsForRegion);

    logger.info(`${debugTag} Number of leading results by party for region ${i}:`, numLeadingByParty);
    logger.info(`${debugTag} Number of elected results by party for region ${i}:`, numElectedByParty);
    logger.info(`${debugTag} Number of total results by party for region ${i}:`, totalElectedByParty);

    // Merge sorted elected results with sorted leading results, which is the ordering needed for the bubble charts.
    const combinedResultsData = [...electedResultsForRegion, ...leadingResultsForRegion];
    // Order group the combined results by party
    const finalResultsArray = reorderResultsDesc(combinedResultsData, sortedPartiesByTotal);
    sortedResultsByRegion[i] = [...finalResultsArray];
  }
  return sortedResultsByRegion;
}

/*
 * Returns whether or not ridingData belongs to the region with regionID
 * @param {Object} ridingData - JSON object representing election data for the riding
 * @param {Number} regionID - number (1 to 13), representing a province/territory
 * @returns {Boolean}
 */
function dataBelongsToRegion(ridingData, regionID) {
  return Number(ridingData.regionID) === regionID;
}

/*
 * Returns whether or not ridingData has a riding result with the elected status.
 * @param {Object} ridingData - JSON object representing election data for the riding
 * @returns {Boolean}
 */
function isElected(ridingData) {
  return ridingData.elected === "Yes";
}

/*
 * Returns whether or not ridingData has a riding result with the leading
 * @param {Object} ridingData - JSON object representing election data for the riding
 * @returns {Boolean}
 */
function isLeading(ridingData) {
  return Number(ridingData.votes) !== 0 && ridingData.elected === "No";
}

/*
 * Returns an array of parties in order of highest to lowest number of seats.
 * @param {Object} resultsByParty -  JSON object representing election data for the riding
 * @returns {Array<String>}
 */
function getOrderedParties(resultsByParty) {
  let parties = Object.keys(partyMappings);
  parties.sort((partyA, partyB) => compareNumSeats(resultsByParty, partyA, partyB));
  return parties;
}

/*
 * Compares the seats in partyA and partyB.
 * @param {Object} resultsByParty -  JSON object representing election data for the riding
 * @returns {Number} 1 if seats in partyA > partyB, -1, if partyA > partyB, and 0 if equal.
 */
function compareNumSeats(resultsByParty, partyA, partyB) {
  // Party B has more seats than party A
  if (resultsByParty[partyA] < resultsByParty[partyB]) {
    return 1;
  }

  // Party A has more seats than party B
  if (resultsByParty[partyA] > resultsByParty[partyB]) {
    return -1;
  }

  // Number of seats are equal
  return 0;
}

/*
 * Reorders resultsForRegion array in party groupings in order of parties with highest to lowest leading/elected
 * ridings.
 * @param {Array<Object>} resultsForRegion - array of JSON objects representing election data for the riding
 * @param {Array<String>} partiesDesc - array of party names in order of highest to lowest leading or elected order for the region
 * @returns {Array<Object>}
 */
function reorderResultsDesc(resultsForRegion, partiesDesc) {
  let reorderedResults = [];

  partiesDesc.forEach((party) => {
    resultsForRegion.forEach((ridingData) => {
      if (ridingData.party === party) {
        // Data pertains to currently higher ranked party so order it at the front of the new sorted array.
        reorderedResults.push(ridingData);
      }
    });
  });

  return reorderedResults;
}

/*
 * Returns color modifier class for bubble representing riding with ridingResult.
 * @param {Object} ridingResult - JSON object representing election data for the riding
 * @returns {String|null} color modifier class for bubble representing riding result or null if riding result is undefined/null
 */
function getColorModifier(ridingResult) {
  if (!ridingResult) {
    return null;
  }
  const status = getStatus(ridingResult);
  let colorModifier = getProp(partyMappings[ridingResult.party], "modifier", null);

  if (status && colorModifier) {
    colorModifier = bubbleClass + "--" + colorModifier + "-" + status;
  }

  return colorModifier;
}

/*
 * Returns status of ridingResult (i,.e., "" for no results, full, or partial)
 * @param {Object} ridingResult - JSON object representing election data for the riding
 * @returns {String}
 */
function getStatus(ridingResult) {
  // In no results state, riding result may be null.
  if (ridingResult && Number(getProp(ridingResult, "votes", 0)) > 0) {
    if (ridingResult.elected === "Yes") {
      return "full";
    } else {
      return "partial";
    }
  }

  return "";
}

/*
 * Populate bubble chart with results (i.e., update bubble colors and screenreader text).
 * @param {Array<Array>} resultsByRegion - array of arrays of JSON objects representing riding results by region to bubblify
 */
function bubblifyResults(resultsByRegion) {
  const regionMappings = {
    1: "nl",
    2: "pe",
    3: "ns",
    4: "nb",
    5: "qc",
    6: "on",
    7: "mb",
    8: "sk",
    9: "ab",
    10: "bc",
    11: "yt",
    12: "nt",
    13: "nu"
  };

  for (let i = 1; i < resultsByRegion.length; i++) {
    const provinceGroup = qs(`.c-election-bubble-chart__province--${regionMappings[i]}`);

    // List of screenreader text describing current region's election results.
    const descriptionList = qs(`#c-election-bubble-chart__bubble-group--${regionMappings[i]}__description ul`);

    const descriptionElement = getById(`c-election-bubble-chart__bubble-group--${regionMappings[i]}__description`);

    let bubbles = null;// Bubble elements for the region

    if (provinceGroup) {
      bubbles = qsa("." + bubbleClass, provinceGroup);
    }

    if (provinceGroup) {
      let bubbleGroupHTML = "";

      // Just an extra fallback in case bubbles don't exist, which should tecnically not be the case
      const numBubbles = bubbles ? bubbles.length : resultsByRegion[i].length;

      // Bubbles for the province/territory.
      for (let j = 0; j < numBubbles; j++) {
        // Color modifier class for the current bubble/riding result.
        const colorModifier = getColorModifier(resultsByRegion[i][j]);
        const bubbleHTML = `<div class="${bubbleClass}${colorModifier ? " " + colorModifier : ""}"></div>`;
        bubbleGroupHTML += bubbleHTML;
      }

      if (descriptionList) {
        // Adds screenreader text to describe election results.
        const numRidingsText = convertToListItem(`${numBubbles} total riding${numBubbles > 1 ? "s" : ""}`);
        const electedDescriptions = getListItems(sortedPartiesByElectedByRegion[i - 1], numElectedByPartyByRegion[i - 1], "elected");
        const leadingDescriptions = getListItems(sortedPartiesByLeadingByRegion[i - 1], numLeadingByPartyByRegion[i - 1], "leading");
        const additionalText = `${electedDescriptions === "" && leadingDescriptions === "" ? "<li>No results</li>" : ""}`;

        logger.info(`${debugTag} electedDescriptions`, electedDescriptions);
        logger.info(`${debugTag} leadingDescriptions`, leadingDescriptions);

        descriptionList.innerHTML = numRidingsText + additionalText + electedDescriptions + leadingDescriptions;
      }

      if (descriptionElement) {
        // Remove all the existing bubbles before inserting the new ones.
        if (bubbles) {
          bubbles.forEach((oldBubble) => {
            oldBubble.remove();
          });
        }

        // Inserts the new set of bubbles right after the screenreader description HTML.
        descriptionElement.insertAdjacentHTML("afterend", bubbleGroupHTML);
      }
    }
  }
}

/*
 * Returns a string of HTML list items containing a description of leading/elected results by party.
 * @param {Array<String>} sortedParties - list of parties in order of highest to lowest leading/elected results.
 * @param {Array<Object>} numByParty - number of leading/elected results by party
 * @param {String} status - status of riding result (i.e., leading or elected)
 * @returns {String}
 */
function getListItems(sortedParties, numByParty, status) {
  let sortedPartiesAndCounts = sortedParties.map((party) => {
    return {
      "partyName": `${partyMappings[party].name}`,
      "numRidings": `${numByParty[party]}`,
      "status": `${status}`
    };
  });

  sortedPartiesAndCounts = sortedPartiesAndCounts.filter((result) => {
    return Number(result.numRidings) !== 0; // Exclude parties with 0 ridings.
  });

  return sortedPartiesAndCounts.map((data) => convertToListItem(getResultDescription(data))).join("");
}

/*
 * Returns "text", as an HTML list item
 * @param {String} text to convert to an HTML list item
 * @returns {String}
 */
function convertToListItem(text) {
  return `<li>${text}</li>`;
}

/*
 * Returns description of riding result. If no result, returns empty string.
 * @param {Object} riding result
 * @returns {String}
 */
function getResultDescription({ partyName, status, numRidings }) {
  return `${numRidings} ${status} ${partyName} candidate${numRidings > 1 ? "s" : ""}`;
}

/*
 * Returns an array of parties and their respective
 * counts of instances/ridings they occur within results.
 * @param {Array<Object>} results - riding results (for the region)
 * @returns {Array<Object>}
 *
 */
function getPartyCounts(results = []) {
  let countsByParty = {
    LIB: 0,
    CON: 0,
    NDP: 0,
    BQ: 0,
    GRN: 0,
    OTH: 0
  };
  if (electionYear === "2019") {
    // PPC was listed as "PPC" in the CP feed for the 2019 election.
    countsByParty.PPC = 0;
  } else {
    // In 2021, CP returned the People's Party results as "PP".
    countsByParty.PP = 0;
  }

  results.forEach(ridingResult => {
    countsByParty[ridingResult.party] = countsByParty[ridingResult.party] + 1;
  });

  return countsByParty;
}
